import DOMPurify from "dompurify";

/**
 * Shared HTML sanitization boundary (CWE-79 / stored XSS).
 *
 * Several backoffice fields are authored in the Froala WYSIWYG editor
 * (`HtmlEditorWidget`) and are legitimately rendered as rich HTML. Because the
 * editor exposes an HTML source view, and because the same values can be written
 * directly through the API, the persisted markup must be treated as untrusted and
 * sanitized at render time before it reaches `dangerouslySetInnerHTML`.
 *
 * The profile below keeps the full HTML5 formatting surface that the editor can
 * produce (headings, lists, tables, links, inline styles) so that sanitization is
 * not a visual regression, while DOMPurify removes the dangerous parts: scripts,
 * embedded/foreign content, `on*` event handlers and `javascript:` style URLs.
 * SVG and MathML are disabled because the editor never emits them and they widen
 * the attack surface.
 */

let hooksRegistered = false;

function registerHooks(): void {
  if (hooksRegistered) {
    return;
  }
  hooksRegistered = true;

  // Prevent reverse tabnabbing on links that authored content opens in a new tab.
  DOMPurify.addHook("afterSanitizeAttributes", node => {
    const element = node as Element;
    if (typeof element.getAttribute !== "function") {
      return;
    }
    if (element.tagName === "A" && element.getAttribute("target")) {
      element.setAttribute("rel", "noopener noreferrer");
    }
  });
}

/**
 * Sanitizes an untrusted HTML fragment.
 *
 * Fails closed: if no DOM is available (so DOMPurify cannot sanitize), an empty
 * string is returned rather than the unsanitized input.
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) {
    return "";
  }
  if (!DOMPurify.isSupported) {
    return "";
  }
  registerHooks();
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
  });
}
